import axios from 'axios'
// import qs from 'qs'
import { toast } from 'vue-sonner'
import { useUserStore } from '@/store/modules/user'

// 请求重试配置
const MAX_RETRY_COUNT = 3 // 最大重试次数
const RETRY_DELAY = 1000 // 重试延迟时间（毫秒）

// Token 刷新状态
let isRefreshing = false // 是否正在刷新 token
interface TokenRefreshSubscriber {
  resolve: (token: string) => void
  reject: (reason: unknown) => void
}

interface RefreshTokenResponse {
  code?: number
  message?: string
  meta?: {
    errCode?: number
    msg?: string
  }
  data?: {
    token?: string
  }
}

class SessionExpiredError extends Error {}

let refreshSubscribers: TokenRefreshSubscriber[] = [] // 等待 token 刷新的请求队列
const MAX_REFRESH_RETRY = 3 // 最大刷新重试次数
const REFRESH_RETRY_DELAY = 3000 // 刷新重试延迟时间（3秒）

// 扩展 AxiosRequestConfig 类型
declare module 'axios' {
  export interface AxiosRequestConfig {
    retry?: boolean
    retryCount?: number
    _retry?: boolean // 标记是否已经重试过（用于 token 刷新）
  }
}

// 添加订阅者到队列
function subscribeTokenRefresh(resolve: (token: string) => void, reject: (reason: unknown) => void) {
  refreshSubscribers.push({ resolve, reject })
}

// Token 刷新成功后，执行队列中的所有请求
function onRefreshed(token: string) {
  refreshSubscribers.forEach(subscriber => subscriber.resolve(token))
  refreshSubscribers = []
}

// Token 刷新失败后，结束所有等待中的请求，避免 Promise 永久挂起。
function onRefreshFailed(reason: unknown) {
  refreshSubscribers.forEach(subscriber => subscriber.reject(reason))
  refreshSubscribers = []
}

function matchesRequestEndpoint(url: string | undefined, endpoint: string) {
  const normalizedUrl = url?.split('?')[0].replace(/\/+$/, '')
  return normalizedUrl === endpoint || normalizedUrl?.endsWith(`/${endpoint}`) === true
}

function isRefreshAuthenticationError(error: unknown) {
  return axios.isAxiosError(error) && error.response?.status === 401
}

function getAuthenticationErrorMessage(error: unknown) {
  if (!axios.isAxiosError(error)) {
    return undefined
  }

  return error.response?.data?.meta?.msg ?? error.response?.data?.message
}

async function handleSessionExpired(reason: unknown, message = '登录已失效，请重新登录') {
  isRefreshing = false
  onRefreshFailed(reason)
  toast.dismiss('token-refresh')
  toast.error(message, { id: 'session-expired' })
  await useUserStore().requestLogout()
}

const api = axios.create({
  baseURL: (import.meta.env.DEV && import.meta.env.VITE_OPEN_PROXY) ? '/proxy/' : import.meta.env.VITE_APP_API_BASEURL,
  timeout: 1000 * 60,
  responseType: 'json',
})

api.interceptors.request.use(
  (request) => {
    // 全局拦截请求发送前提交的参数
    const userStore = useUserStore()
    // 设置请求头
    if (request.headers) {
      if (userStore.isLogin) {
        request.headers.Authorization = `Bearer ${userStore.token}`
      }
    }
    // 是否将 POST 请求参数进行字符串化处理
    if (request.method === 'post') {
      // request.data = qs.stringify(request.data, {
      //   arrayFormat: 'brackets',
      // })
    }
    return request
  },
)

// 处理错误信息的函数
async function handleError(error: any) {
  const originalRequest = error.config

  // 422验证错误 - 显示服务器返回的错误信息
  if (error.response?.status === 422) {
    const message = error.response?.data?.meta?.msg || error.response?.data?.message || '参数验证失败'
    toast.error(message)
    return Promise.reject(error)
  }

  // 检查是否是401错误（Token 过期或无效）
  if (error.response?.status === 401) {
    if (!originalRequest) {
      return Promise.reject(error)
    }

    const isLoginRequest = matchesRequestEndpoint(originalRequest.url, 'login')
    const isRefreshRequest = matchesRequestEndpoint(originalRequest.url, 'refresh')
    const responseData = error.response.data
    const errorMessage = responseData?.meta?.msg ?? responseData?.message

    // 登录接口的401错误，显示具体错误信息
    if (isLoginRequest) {
      toast.error(errorMessage || '用户名或密码错误')
      return Promise.reject(error)
    }

    // 刷新接口返回 401/10009 时，旧 Token 已无法再刷新。
    if (isRefreshRequest) {
      await handleSessionExpired(error, errorMessage || '登录已失效，请重新登录')
      return Promise.reject(error)
    }

    // 普通业务接口返回 401/401 时，先使用旧 Access Token 换取新 Token。
    if (!originalRequest._retry) {
      if (isRefreshing) {
        // 如果正在刷新 token，将请求加入队列
        originalRequest._retry = true
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh(
            (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`
              resolve(api(originalRequest))
            },
            reject,
          )
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      // 尝试刷新 token，最多重试 3 次
      const attemptRefresh = async (retryCount: number): Promise<any> => {
        try {
          // 显示重试提示
          if (retryCount > 0) {
            toast.loading(`重新连接服务器... (${retryCount}/${MAX_REFRESH_RETRY})`, {
              id: 'token-refresh',
            })
          }

          const userStore = useUserStore()
          const res = await axios.post<RefreshTokenResponse>(
            'refresh',
            {},
            {
              baseURL: api.defaults.baseURL,
              headers: {
                Authorization: `Bearer ${userStore.token}`,
              },
            },
          )

          const refreshSucceeded = res.data.meta?.errCode === 0 || res.data.code === 0
          const newToken = res.data.data?.token
          if (refreshSucceeded && newToken) {
            // 更新 token
            userStore.setToken(newToken)

            // 更新原始请求的 token
            originalRequest.headers.Authorization = `Bearer ${userStore.token}`

            // 执行队列中的请求
            onRefreshed(userStore.token)
            isRefreshing = false

            // 关闭提示
            toast.dismiss('token-refresh')

            // 重新发起原始请求
            return api(originalRequest)
          }
          else {
            throw new SessionExpiredError(res.data.meta?.msg || res.data.message || '刷新失败')
          }
        }
        catch (refreshError: any) {
          if (refreshError instanceof SessionExpiredError) {
            await handleSessionExpired(refreshError, refreshError.message)
            return Promise.reject(refreshError)
          }

          if (isRefreshAuthenticationError(refreshError)) {
            await handleSessionExpired(
              refreshError,
              getAuthenticationErrorMessage(refreshError) || '登录已失效，请重新登录',
            )
            return Promise.reject(refreshError)
          }

          // 如果还有重试次数，延迟后重试
          if (retryCount < MAX_REFRESH_RETRY) {
            await new Promise(resolve => setTimeout(resolve, REFRESH_RETRY_DELAY))
            return attemptRefresh(retryCount + 1)
          }
          else {
            // 重试次数用完，清理状态并跳转登录
            await handleSessionExpired(refreshError)
            return Promise.reject(refreshError)
          }
        }
      }

      return attemptRefresh(0)
    }

    // Token 刷新后重放请求仍然 401，说明新 Token 也无效。
    await handleSessionExpired(error, errorMessage || '登录状态已失效，请重新登录')
    return Promise.reject(error)
  }

  // 处理其他类型的错误
  let message = error.message
  if (message === 'Network Error') {
    message = '后端网络故障'
  }
  else if (message.includes('timeout')) {
    message = '接口请求超时'
  }
  else if (message.includes('Request failed with status code')) {
    message = `接口${message.substr(message.length - 3)}异常`
  }
  toast.error('Error', {
    description: message,
  })
  return Promise.reject(error)
}

api.interceptors.response.use(
  async (response) => {
    /**
     * 全局拦截请求发送后返回的数据，适配API响应格式
     * API响应格式：{ meta: { errCode: 0, msg: '' }, data: {} }
     * 规则是当 errCode 为 0 时表示请求成功
     * 请求出错时 msg 会返回错误信息
     */
    const { meta } = response.data

    if (meta?.errCode === 0) {
      // 请求成功
      return Promise.resolve(response.data)
    }
    else {
      const isLoginRequest = matchesRequestEndpoint(response.config.url, 'login')
      // 请求失败，显示错误信息
      toast.error(meta?.msg || (isLoginRequest ? '用户名或密码错误' : '请求失败'))
      return Promise.reject(response.data)
    }
  },
  async (error) => {
    // 获取请求配置
    const config = error.config

    // 如果配置不存在或未启用重试，则直接处理错误
    if (!config || !config.retry) {
      return handleError(error)
    }

    // 设置重试次数
    config.retryCount = config.retryCount || 0

    // 判断是否超过重试次数
    if (config.retryCount >= MAX_RETRY_COUNT) {
      return handleError(error)
    }

    // 重试次数自增
    config.retryCount += 1

    // 延迟重试
    await new Promise(resolve => setTimeout(resolve, RETRY_DELAY))

    // 重新发起请求
    return api(config)
  },
)

// 导出HTTP实例
export const http = api
export default api

// 导出API模块
export * from './modules/admin'
export * from './modules/announcement'
export { default as announcementApi } from './modules/announcement'
export * from './modules/contract'
export { default as franchiseContractApi } from './modules/contract'
export * from './modules/link'
export { default as linkApi } from './modules/link'
export * from './modules/menu'
export * from './modules/message'
export { default as messageApi } from './modules/message'
export * from './modules/modulePermission'
export * from './modules/product'
export { productApi } from './modules/product'
export * from './modules/role'
export * from './modules/settings'
export { default as settingsApi } from './modules/settings'
export * from './modules/student'
export * from './modules/studentLead'
export * from './modules/studentLeadFollow'
export * from './modules/systemModule'
export { default as systemModuleAPI } from './modules/systemModule'
export * from './modules/user'
export * from './modules/workflow'
export { workflowApi } from './modules/workflow'
